// One board-level read of twilio_messages for a set of deal ids (Oct 16, 2026).
//
// The last 90 days, newest first, reduced to the newest text per deal (see
// commsStrip.ts). PostgREST caps a response at 1,000 rows, and the Supplements
// board's deals already hold ~830 texts in 90 days, so the read pages until every
// deal has its newest row or the rows run out. Returns null on any error: the
// caller hides the strips and keeps the board (same rule as the "supps sent" chip).

import { supabase } from "./supabase";
import { reduceCommsMap, type CommsMap, type CommsStripRow } from "./commsStrip";

const PAGE = 1000;

export async function fetchCommsMap(
  dealIds: string[],
  opts: { days?: number; maxPages?: number } = {},
): Promise<CommsMap | null> {
  if (dealIds.length === 0) return {};
  const days = opts.days ?? 90;
  const maxPages = opts.maxPages ?? 5;
  const since = new Date(Date.now() - days * 86_400_000).toISOString();
  const rows: CommsStripRow[] = [];
  const pending = new Set(dealIds);
  let from = 0;
  for (let page = 0; page < maxPages; page++) {
    const { data, error, count } = await supabase
      .from("twilio_messages")
      .select("deal_id,direction,body,created_at,sent_at", { count: "exact" })
      .in("deal_id", dealIds)
      .gte("created_at", since)
      .order("created_at", { ascending: false, nullsFirst: false })
      .order("id", { ascending: false })
      .range(from, from + PAGE - 1);
    if (error) return null;
    const batch = (data ?? []) as CommsStripRow[];
    if (batch.length === 0) break;
    rows.push(...batch);
    for (const r of batch) if (r.deal_id) pending.delete(r.deal_id);
    from += batch.length;
    const total = count ?? from;
    if (pending.size === 0 || from >= total) break;
  }
  return reduceCommsMap(rows);
}
